const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const ARRAY_IDENTITY_KEYS = ["id", "kernel_id", "kernelId", "command"];
const ARRAY_INDEX = /^(0|[1-9]\d*)$/;
const ROOT_KEYS = new Set([
    "agentic",
    "command",
    "description",
    "items",
    "params",
    "render",
    "renderSource",
    "rows",
    "scopeMode",
    "slug",
    "steps",
    "title",
]);
const STEP_KEYS = new Set([
    "args",
    "argsFrom",
    "code",
    "entry",
    "id",
    "inputs",
    "kind",
    "outputSchema",
    "prompt",
    "role",
    "server",
    "spec",
    "tool",
]);
const CELL_KEYS = new Set(["caption", "command", "kernel_id", "kernelId", "width"]);

function isObject(value) {
    return value !== null && typeof value === "object";
}

function pathFor(parent, key) {
    return ARRAY_INDEX.test(key)
        ? `${parent}[${key}]`
        : `${parent}.${key}`;
}

function enumerableDataDescriptors(value, path, { rejectDangerous }) {
    if (!isObject(value)) return [];
    const descriptors = Object.getOwnPropertyDescriptors(value);
    const entries = [];
    for (const key of Object.keys(descriptors)) {
        const descriptor = descriptors[key];
        if (!descriptor.enumerable) continue;
        if (FORBIDDEN_KEYS.has(key)) {
            if (rejectDangerous) {
                throw new TypeError(
                    `tenant view definition contains forbidden key ${JSON.stringify(key)} at ${path}`,
                );
            }
            continue;
        }
        if (!Object.prototype.hasOwnProperty.call(descriptor, "value")) {
            if (rejectDangerous) {
                throw new TypeError(
                    `tenant view definition contains an accessor at ${pathFor(path, key)}`,
                );
            }
            continue;
        }
        entries.push([key, descriptor.value]);
    }
    return entries;
}

function defineData(target, key, value) {
    Object.defineProperty(target, key, {
        configurable: true,
        enumerable: true,
        writable: true,
        value,
    });
}

export function readOwnDataProperty(value, key) {
    if (!isObject(value)) return undefined;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor && Object.prototype.hasOwnProperty.call(descriptor, "value")
        ? descriptor.value
        : undefined;
}

export function copyOwnEnumerableDataProperties(
    value,
    { omit = [] } = {},
) {
    const omitted = new Set(omit);
    const copy = {};
    for (const [key, child] of enumerableDataDescriptors(
        value,
        "$",
        { rejectDangerous: false },
    )) {
        if (!omitted.has(key)) defineData(copy, key, child);
    }
    return copy;
}

function cloneDefinition(value, path, ancestors) {
    if (!isObject(value)) return value;
    if (ancestors.has(value)) {
        throw new TypeError(`tenant view definition contains a cycle at ${path}`);
    }
    ancestors.add(value);
    const clone = Array.isArray(value) ? [] : {};
    for (const [key, child] of enumerableDataDescriptors(
        value,
        path,
        { rejectDangerous: true },
    )) {
        defineData(clone, key, cloneDefinition(child, pathFor(path, key), ancestors));
    }
    if (Array.isArray(value)) {
        const length = readOwnDataProperty(value, "length");
        if (Number.isSafeInteger(length) && length >= 0) clone.length = length;
    }
    ancestors.delete(value);
    return clone;
}

export function cloneTenantViewDefinition(value) {
    return cloneDefinition(value, "$", new WeakSet());
}

function assertDefinitionKeys(value, path, ancestors) {
    if (!isObject(value)) return;
    if (ancestors.has(value)) {
        throw new TypeError(`tenant view definition contains a cycle at ${path}`);
    }
    ancestors.add(value);
    for (const [key, child] of enumerableDataDescriptors(
        value,
        path,
        { rejectDangerous: true },
    )) {
        assertDefinitionKeys(child, pathFor(path, key), ancestors);
    }
    ancestors.delete(value);
}

function canvasRowCellIdentities(value) {
    const cells = readOwnDataProperty(value, "cells");
    if (!Array.isArray(cells)) return null;
    const length = readOwnDataProperty(cells, "length");
    if (!Number.isSafeInteger(length) || length === 0) return null;
    const cellIdentities = [];
    for (let index = 0; index < length; index += 1) {
        const cellIdentity = identity(
            readOwnDataProperty(cells, String(index)),
            "$.rows[].cells",
        );
        if (!cellIdentity) return null;
        cellIdentities.push(JSON.stringify(cellIdentity));
    }
    return cellIdentities;
}

function canvasRowIdentity(value) {
    const cellIdentities = canvasRowCellIdentities(value);
    if (!cellIdentities) return null;
    cellIdentities.sort();
    return ["cells", JSON.stringify(cellIdentities)];
}

function canvasRowIdentityOverlap(left, right) {
    const leftIdentities = canvasRowCellIdentities(left);
    const rightIdentities = canvasRowCellIdentities(right);
    if (!leftIdentities || !rightIdentities) return 0;
    const available = new Map();
    for (const candidate of leftIdentities) {
        available.set(candidate, (available.get(candidate) || 0) + 1);
    }
    return rightIdentities.reduce((count, candidate) => {
        const remaining = available.get(candidate) || 0;
        if (remaining === 0) return count;
        available.set(candidate, remaining - 1);
        return count + 1;
    }, 0);
}

function identity(value, path) {
    if (!isObject(value) || Array.isArray(value)) return null;
    for (const key of ARRAY_IDENTITY_KEYS) {
        const candidate = readOwnDataProperty(value, key);
        if (
            (typeof candidate === "string" && candidate.length > 0)
            || typeof candidate === "number"
        ) {
            return [key, candidate];
        }
    }
    if (path === "$.rows") return canvasRowIdentity(value);
    return null;
}

function matchingArrayBase(base, editedValue, index, used, reserved, path) {
    const wanted = identity(editedValue, path);
    if (wanted) {
        return reserved.get(index);
    }
    if (used.has(index)) return undefined;
    used.add(index);
    return readOwnDataProperty(base, String(index));
}

function reserveIdentityArrayBases(base, edited, editedLength, used, path) {
    const reserved = new Map();
    const baseLength = readOwnDataProperty(base, "length") ?? 0;
    for (let index = 0; index < editedLength; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(edited, String(index));
        if (
            !descriptor?.enumerable
            || !Object.prototype.hasOwnProperty.call(descriptor, "value")
        ) {
            continue;
        }
        const wanted = identity(descriptor.value, path);
        if (!wanted) continue;
        for (let candidateIndex = 0; candidateIndex < baseLength; candidateIndex += 1) {
            if (used.has(candidateIndex)) continue;
            const candidate = readOwnDataProperty(base, String(candidateIndex));
            const candidateIdentity = identity(candidate, path);
            if (
                candidateIdentity
                && candidateIdentity[0] === wanted[0]
                && candidateIdentity[1] === wanted[1]
            ) {
                // Reserve every stable-identity match before positional items
                // can claim a base slot needed by a later edited element.
                used.add(candidateIndex);
                reserved.set(index, candidate);
                break;
            }
        }
    }
    if (path !== "$.rows") return reserved;
    const overlaps = [];
    for (let index = 0; index < editedLength; index += 1) {
        if (reserved.has(index)) continue;
        const editedValue = readOwnDataProperty(edited, String(index));
        for (let candidateIndex = 0; candidateIndex < baseLength; candidateIndex += 1) {
            if (used.has(candidateIndex)) continue;
            const candidate = readOwnDataProperty(base, String(candidateIndex));
            const score = canvasRowIdentityOverlap(editedValue, candidate);
            if (score > 0) overlaps.push([score, index, candidateIndex, candidate]);
        }
    }
    overlaps.sort((left, right) =>
        right[0] - left[0]
        || left[1] - right[1]
        || left[2] - right[2]);
    for (const [, index, candidateIndex, candidate] of overlaps) {
        if (reserved.has(index) || used.has(candidateIndex)) continue;
        used.add(candidateIndex);
        reserved.set(index, candidate);
    }
    return reserved;
}

function canvasRowMergeBase(matchedRow, rows) {
    const result = {};
    for (const [key, value] of enumerableDataDescriptors(
        matchedRow,
        "$.rows[]",
        { rejectDangerous: true },
    )) {
        defineData(result, key, value);
    }
    const cells = [];
    const rowCount = readOwnDataProperty(rows, "length") ?? 0;
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const rowCells = readOwnDataProperty(
            readOwnDataProperty(rows, String(rowIndex)),
            "cells",
        );
        if (!Array.isArray(rowCells)) continue;
        const cellCount = readOwnDataProperty(rowCells, "length") ?? 0;
        for (let cellIndex = 0; cellIndex < cellCount; cellIndex += 1) {
            cells.push(readOwnDataProperty(rowCells, String(cellIndex)));
        }
    }
    defineData(result, "cells", cells);
    return result;
}

function knownKeysAt(path) {
    if (path === "$") return ROOT_KEYS;
    if (/^\$\.steps\[\d+\]$/.test(path)) return STEP_KEYS;
    if (/^\$\.rows\[\d+\]$/.test(path)) return new Set(["cells"]);
    if (/^\$\.(?:rows\[\d+\]\.cells|items)\[\d+\]$/.test(path)) return CELL_KEYS;
    return null;
}

function isUserDefinedMap(path) {
    return path === "$.params"
        || path === "$.render"
        || /^\$\.steps\[\d+\]\.(?:args|inputs|outputSchema|spec)$/.test(path);
}

function mergeDefinitions(base, edited, path) {
    if (!isObject(edited)) return edited;
    if (isUserDefinedMap(path)) {
        return cloneDefinition(edited, path, new WeakSet());
    }
    if (Array.isArray(edited)) {
        const result = [];
        const editedLength = readOwnDataProperty(edited, "length") ?? 0;
        const baseArray = Array.isArray(base) ? base : [];
        const used = new Set();
        const reserved = reserveIdentityArrayBases(
            baseArray,
            edited,
            editedLength,
            used,
            path,
        );
        for (let index = 0; index < editedLength; index += 1) {
            const key = String(index);
            const editedDescriptor = Object.getOwnPropertyDescriptor(edited, key);
            if (
                !editedDescriptor?.enumerable
                || !Object.prototype.hasOwnProperty.call(editedDescriptor, "value")
            ) {
                continue;
            }
            const baseValue = matchingArrayBase(
                baseArray,
                editedDescriptor.value,
                index,
                used,
                reserved,
                path,
            );
            const mergeBase = path === "$.rows"
                ? canvasRowMergeBase(baseValue, baseArray)
                : baseValue;
            defineData(
                result,
                key,
                mergeDefinitions(mergeBase, editedDescriptor.value, pathFor(path, key)),
            );
        }
        result.length = editedLength;
        return result;
    }

    const result = isObject(base) && !Array.isArray(base)
        ? cloneDefinition(base, path, new WeakSet())
        : {};
    const editedEntries = enumerableDataDescriptors(
        edited,
        path,
        { rejectDangerous: true },
    );
    const editedKeys = new Set(editedEntries.map(([key]) => key));
    for (const key of knownKeysAt(path) || []) {
        if (!editedKeys.has(key)) delete result[key];
    }
    for (const [key, editedValue] of editedEntries) {
        const baseValue = readOwnDataProperty(base, key);
        defineData(
            result,
            key,
            mergeDefinitions(baseValue, editedValue, pathFor(path, key)),
        );
    }
    return result;
}

export function mergeSameVersionTenantViewDefinition(base, edited) {
    assertDefinitionKeys(base, "$", new WeakSet());
    assertDefinitionKeys(edited, "$", new WeakSet());
    return mergeDefinitions(base, edited, "$");
}
